//import EventStream from "Models/EventStream";
import { EventStreamPhaser2 } from "Phaser/GameObjects/CursorMobile";
import { IteratorExtensible } from "Phaser/GameObjects/CursorMobile";
import Phaser from "phaser";
import { ErrorFeedbackManager } from "Phaser/GameObjects/ErrorFeedbackManager";
import {
  CurrentSceneReady,
  ListenAddPhrase,
  ListenForReactEvent,
  StartedPlaying,
  ListenPulseNote,
  ListenChangeTempo,
  ListenSetNoteState,
  ListenStartExercise,
  NotifyReact,
  ReactToExerciseEventType,
  RemoveListener,
  ListenMidiOn,
  ListenMidiOff,
  ListenPauseExercise,
  ShowToast,
  ListenSetCursor,
  ListenResizeWindow,
  ExercisePaused,
  SetPhrasesTillNextTier,
  ListenSetFreePlayMode,
  SetPhraseEligible,
} from "Phaser/EventBus";
import CursorMobile from "Phaser/GameObjects/CursorMobile";
import IPlayable from "Phaser/GameObjects/Playable";
import {
  DefaultConfig,
  getPhraseEnd,
  PhraseData,
  PhraseGraphic,
} from "Phaser/GameObjects/StaffLine";
import { PrerenderedPhraseData } from "Types";
import StaffExercise, { INoteGraphicsDatabase } from "./StaffExercise";
import { ProgressState } from "Phaser/GameObjects/Progress";
import Phrase, { Iterator } from "Models/Phrase";
import TimeKeeper from "Models/TimeKeeper";
import * as Tone from "tone";
import Logger from "Utils/Logger";
import { NoteType } from "@museflow-ai/opensheetmusicdisplay";
import TimeSignature from "Models/TimeSignature";
import { clamp } from "lodash";
import ChevronManager from "Phaser/Gameplay/ChevronManager";
import { COLORBLIND_TYPE } from "Utils/Constants";
import { optimizeStaffLineHeight } from "Phaser/GameObjects/StaffLine/Util";

class PlayablePhrase {
  cursor?: CursorMobile;
  graphic: PhraseGraphic;
  idx: number = 0;
  isActive: boolean = false;

  constructor(graphic: PhraseGraphic, cursor?: CursorMobile) {
    this.cursor = cursor;
    this.graphic = graphic;
  }

  activate(unpause?: boolean) {
    this.isActive = true;
    this.cursor?.enable();
    //this.cursor?.setAlpha();
    if (unpause) this.cursor?.unpause();
  }

  deactivate() {
    this.isActive = false;
    this.cursor?.disable();
  }

  pause() {
    this.cursor?.pause();
  }

  update() {
    this.graphic.update();
    if (this.isActive) this.cursor?.update();
  }

  destroy() {
    this.graphic.destroy();
    this.cursor?.destroy();
  }
}

export default class SightReadingPhaser
  extends Phaser.Scene
  implements INoteGraphicsDatabase
{
  phrases: (PlayablePhrase | undefined)[] = [];
  mainIterator!: IteratorExtensible;
  //cursors: CursorMobile[] = [];
  currentCursor: number = 0;
  currentIdx?: number;
  eventStream?: EventStreamPhaser2;
  log?: Phaser.GameObjects.Text;

  timeKeeperAuxiliary?: TimeKeeper;

  phrasesSeen: Map<string, number> = new Map();

  clickAudio?: Phaser.Sound.BaseSound;
  tempo: number = 72;

  lastTickTime?: number;
  lastMeasure?: number;

  debugText?: Phaser.GameObjects.Text;

  // This is a hack for maintaining the correct sequence when new phrases get loaded in
  // should no longer be necessary once we get all the data baked in from the backend
  phraseLoadQueue: {
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
    phraseRaw: Phrase;
  }[] = [];

  pauseQueued = false;
  playbackStarted = false;
  shouldUpdate = false;

  measureCount: number = 0;

  mobileView: boolean = false;
  width: number = 0;
  height: number = 0;
  private firstPhrase = true;

  topPhrase: number = 0;
  bottomPhrase: number = 1;

  private activePhrase = 0;

  getActivePhrase(): number {
    return this.activePhrase;
  }
  private phrasesTillNextTier = 4;
  private freePlayMode: boolean = false;

  private gameplayStarted: boolean = false;

  private eventsQueue: [number, number][] = [];
  private startPlayingTriggerEventTimestamp?: number;

  private nextPhraseStartTimer?: Phaser.Time.TimerEvent;

  private chevronManager?: ChevronManager;
  private errorFeedbackManager?: ErrorFeedbackManager;
  private phrase1Gizmo?: Phaser.GameObjects.Rectangle;
  private phrase2Gizmo?: Phaser.GameObjects.Rectangle;

  constructor() {
    super("SightReadingPhaser");
  }

  create() {
    NotifyReact(CurrentSceneReady(this));
    this.events.once("shutdown", this.destroy, this);
  }

  debugLog(text: string) {
    this.debugText!.setText(text);
  }

  init({
    eventStream,
    mobile,
    width,
    height,
  }: {
    eventStream: EventStreamPhaser2;
    mobile?: boolean;
    width: number;
    height: number;
  }) {
    this.chevronManager = new ChevronManager(eventStream);
    this.errorFeedbackManager = new ErrorFeedbackManager(this);
    this.width = width;
    this.height = height;
    this.mobileView = mobile ?? false;
    this.debugText = this.add.text(100, 100, "DebugLog", {
      fontSize: 12,
      color: "#000000",
    });
    this.timeKeeperAuxiliary = new TimeKeeper();
    //this.cameras.main.setZoom(0.5);
    this.eventStream = eventStream;
    // Set error feedback manager on event stream
    this.eventStream.setErrorFeedbackManager(this.errorFeedbackManager, this);
    this.phrases = [];
    this.cameras.main.setBackgroundColor("0xebebeb");
    //this.log = this.add.text(0, 0, "log");
    ListenForReactEvent(ListenResizeWindow(this.resize), this);
    ListenForReactEvent(ListenPauseExercise(this.pauseExercise), this);
    ListenForReactEvent(ListenAddPhrase(this.addPhrase), this, false, true);
    ListenForReactEvent(ListenMidiOn(this.noteOn), this);
    ListenForReactEvent(ListenMidiOff(this.noteOff), this);
    ListenForReactEvent(
      ListenSetNoteState(this.setNoteState),
      this,
      false,
      false,
    );
    ListenForReactEvent(ListenPulseNote(this.pulseNote), this, false, false);
    ListenForReactEvent(
      ListenSetNoteState(this.setNoteState),
      this,
      false,
      false,
    );
    ListenForReactEvent(
      ListenStartExercise(this.startExercise),
      this,
      false,
      false,
    );
    ListenForReactEvent(
      ListenChangeTempo(this.changeTempo),
      this,
      false,
      false,
    );
    //ListenForReactEvent(ListenAddPhrase(this.addPhrase), this, false, true);
    ListenForReactEvent(ListenSetCursor(this.setCursor), this);
    ListenForReactEvent(ListenSetFreePlayMode(this.setFreePlayMode), this);
    this.clickAudio = this.sound.add("click");
    /*this.phrase1Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase2Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase1Gizmo?.setOrigin(0, 0);
    this.phrase2Gizmo?.setOrigin(0, 0);*/
  }

  setFreePlayMode({ enabled }: { enabled: boolean }) {
    this.freePlayMode = enabled;
  }

  startExerciseDummy() {
    NotifyReact(StartedPlaying());
  }

  setCursor(props: { measure?: number }) {
    if (!this.playbackStarted) return;
    const measure = Math.max(
      this.phrases[this.activePhrase]!.cursor!.getLastPlayedMeasure() - 1,
      0,
    );
    if (measure === -1) return;
    console.debug("setting cursor", measure);
    const phraseIdx = this.phrases.findIndex((phrase) =>
      phrase?.graphic.containsMeasure(measure),
    );
    //console.debug('current measure', measure, phrase?.graphic.phraseStartingMeasure);
    if (phraseIdx === undefined) {
      console.error(
        "given measure doesn't appear in any of the active phrases!",
      );
    } else {
      const phrase = this.phrases[phraseIdx]!;
      if (phrase !== this.phrases[this.activePhrase]) {
        const current = this.phrases[this.activePhrase]!;
        const firstNotePos =
          this.phrases[this.activePhrase]!.graphic.getFirstNotePosition();
        //this.phrases[this.activePhrase]?.cursor?.
        current.cursor?.setStart(
          firstNotePos,
          current.graphic._data.startTimestamp,
          0,
        );
        //this.phrases[this.activePhrase]?.cursor?.setLastNotePos(firstNotePos);
        current.cursor?.setX(firstNotePos - current.cursor!.width * 2);
        current?.deactivate();
        this.activePhrase = phraseIdx;
        console.debug("setting active phrase", phraseIdx);
      }
      phrase.activate();
      const firstNote = phrase.graphic.getFirstNotePositionInMeasure(
        measure - phrase.graphic.phraseStartingMeasure!,
      );
      console.debug(
        "current got first note",
        firstNote,
        measure,
        phrase.graphic.phraseStartingMeasure,
      );
      const cursorStartPos = firstNote!;
      //let cursor = this.cursors[this.currentCursor];
      const numMeasuresIn = measure - phrase.graphic.phraseStartingMeasure!;
      const timesig = phrase.graphic._data.timeSignature;
      /*if (cursor.startingMeasure > measure) {
        const phrase = this.phrases[this.currentIdx!]!;
        const timestamp = phrase.graphic._data.startTimestamp;
        const firstNote = phrase.graphic.getFirstNotePosition();
        cursor.setAlpha(0);
        cursor.setStart(firstNote, timestamp, 0);
        this.cursors[this.currentCursor].setX(
          this.phrases[this.currentIdx!]?.graphic.getPhraseStartX()!
        );
        this.currentCursor = (this.currentCursor + 1) % 2;
        cursor = this.cursors[this.currentCursor];
      }*/
      //cursor.clearBlink();
      let cursor = this.phrases[this.activePhrase]!.cursor!;
      cursor.lastPlayedMeasure = measure - cursor.startingMeasure;
      cursor.setStart(
        cursorStartPos,
        (timesig.numerator / timesig.denominator) * numMeasuresIn,
        measure - cursor.startingMeasure,
      );
    }
  }

  changeTempo({ tempo }: { tempo: number }) {
    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);
    console.debug("changed tempo to", tempo);
  }

  metronomeClick() {
    if (this.clickAudio === undefined) {
      Logger.warn("metronome audio not loaded!");
    }
    this.clickAudio?.stop();
    this.clickAudio?.play();
  }

  startExercise({ tempo }: { tempo?: number }) {
    if (tempo) {
      this.tempo = tempo;
    }
    this.shouldUpdate = true;
    if (!this.playbackStarted) {
      //Tone.start();
      this.metronomeClick();
      const secondsPerMeasure = (60 / this.tempo) * 4;
      const measureOffset =
        TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      Tone.start();
      //NotifyReact(StartedPlaying());
      this.timeKeeperAuxiliary!.setTimeSignatures(0, [new TimeSignature(4, 4)]);
      this.timeKeeperAuxiliary!.resetTime();
      this.timeKeeperAuxiliary!.unpause(
        this.tempo,
        0,
        measureOffset,
        NoteType._32nd,
      );
      this.phrases[0]!.activate();
      //this.phrases[0]!.cursor!.setAlpha(1);
      this.phrases[0]!.cursor!.blink((60 / this.tempo) * 1000);
      //this.cursors[this.currentCursor].blink((60 / this.tempo) * 1000);
      //NotifyReact(StartedPlaying());
      //this.playbackStarted = true;
      //this.cursors[this.currentCursor].unpause();
    } else {
      NotifyReact(StartedPlaying());
      this.phrases[this.activePhrase]?.activate();
      this.phrases[this.activePhrase]?.cursor?.unpause();
    }
  }

  // runs every 'tick'
  tickUpdate(beatDuration: number) {
    if (this.playbackStarted) {
      if (!this.freePlayMode) this.chevronManager?.onTick();
    } else {
      this.metronomeClick();
      this.phrases[this.activePhrase]?.cursor?.blink(beatDuration);
    }
  }

  update() {
    if (this.pauseQueued) {
      this._pauseExercise();
    }
    if (this.shouldUpdate) {
      this.phrases.forEach((p) => p?.update());
      const timeKeeper = this.playbackStarted
        ? this.eventStream?.timeKeeper
        : this.timeKeeperAuxiliary;
      if (timeKeeper === undefined) {
        Logger.warn("timekeeper unavailable!");
        return;
      }
      const timestamp = timeKeeper.audioTimeToMeasureTimestamp();
      if (this.lastTickTime === undefined) {
        this.lastTickTime =
          timestamp - TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME;
      }
      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      if (measureDurationSinceLastTickTime > beatFraction) {
        const beatDuration = (60 / (this.tempo ?? 72)) * 1000;
        this.tickUpdate(beatDuration);
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
      }
    }
  }

  showToast(message: string) {
    console.debug(message);
  }

  onPlayingLastSlice(start: number, duration: number, durationInSecs: number) {
    console.debug("playing last slice");
    if (!this.mobileView) {
      const nextPhrase = (this.activePhrase + 1) % 4;
      const phrase = this.phrases[nextPhrase];
      if (phrase) {
        // Reset cursor position to the start of the phrase BEFORE making it visible
        // to prevent a flash where the cursor appears at its previous position
        const firstNotePos = phrase.graphic.getFirstNotePosition();
        phrase.cursor?.setX(firstNotePos - (phrase.cursor?.width ?? 0));
        phrase.activate();
        phrase.cursor?.startCountingIn(start, duration, duration);
      }
    }
  }

  onFinishPlayingPhrase(eligible?: boolean) {
    const acc = this.eventStream?.calcAccuracy()!;
    console.log("PTNT: finished playing the phrase", acc, eligible);
    if (!this.freePlayMode) {
      this.chevronManager?.onPhraseFinishPlaying();
    }
    // disable current cursor and active the next one
    if (!this.mobileView) {
      //this.currentCursor = (this.currentCursor + 1) % 2;
      //this.cursors[this.currentCursor].unpause();
      this.phrases[this.activePhrase]?.deactivate();
      this.activePhrase = (this.activePhrase + 1) % 4;
      console.debug("setting active phrase", this.activePhrase);
      this.phrases[this.activePhrase]?.activate(true);
      this.phrases[this.activePhrase]?.cursor?.setEligible(acc >= 95);
      //this.phrases[this.activePhrase]?.cursor?.unpause();
    }
  }

  addPhrase(phraseData: {
    phraseRaw: Phrase;
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
  }) {
    let phraseToLoad = phraseData;
    // Do not add a phrase if its starting timestamp is found to be earlier than the phrase that is
    // currently being played because it is probably a duplicate addPhrase call.
    if (
      this.phrases[this.activePhrase] &&
      phraseToLoad.phrase.startTimestamp <=
        this.phrases[this.activePhrase]!.cursor!.getTimestamp()
    ) {
      return;
    }
    // Check if we have pending phrases in the load queue that need to be loaded BEFORE the phrase that
    // is currently being requested to load (the load queue is assumed to always be sorted)
    this.phraseLoadQueue = this.phraseLoadQueue.sort(
      (a, b) => b.phrase.startTimestamp - a.phrase.startTimestamp,
    );
    const inQueue = this.phraseLoadQueue.at(0);
    if (
      inQueue &&
      inQueue.phrase.startTimestamp < phraseToLoad.phrase.startTimestamp
    ) {
      phraseToLoad = inQueue;
      this.phraseLoadQueue[0] = phraseData;
    }
    const { phrase, phraseRaw, cursorData } = phraseToLoad;
    if (
      phraseToLoad &&
      this.phrasesSeen.get(phrase.uuid) !== phrase.startTimestamp
    ) {
      if (phrase.startTimestamp !== 0 && this.phrases.length === 0) {
        this.phraseLoadQueue.push({ phrase, cursorData, phraseRaw });
        return;
      }
      if (this.currentIdx) {
        const nextPhrase =
          this.phrases[(this.currentIdx + 3) % 4]?.graphic._data;
        if (this.phrases[this.activePhrase]) {
          const firstPhrasePlaying =
            this.phrases.length == 2 &&
            this.phrases[this.activePhrase]!.cursor!.getTimestamp() <
              getPhraseEnd(this.phrases[0]!.graphic._data);
          if (
            firstPhrasePlaying ||
            (nextPhrase &&
              this.phrases[this.activePhrase]!.cursor!.getTimestamp() <
                getPhraseEnd(nextPhrase))
          ) {
            this.phraseLoadQueue.push({ phrase, cursorData, phraseRaw });
            return;
          } else if (this.phrases[this.activePhrase] && nextPhrase) {
          }
        }
      }
      const x = this.mobileView
        ? this.currentIdx !== undefined
          ? this.phrases[this.currentIdx]!.graphic.getNextPosition()
          : 100
        : 100;
      const _idx = this.currentIdx ?? 0;
      if (_idx % 2 === 0) {
        this.topPhrase = _idx;
      } else {
        this.bottomPhrase = _idx;
      }
      this.currentIdx =
        this.currentIdx !== undefined ? (this.currentIdx + 1) % 4 : 0;
      phrase.showClef = !this.mobileView || phrase.startTimestamp === 0;
      phrase.showBrace = !this.mobileView || phrase.startTimestamp === 0;
      phrase.showTimeSig = phrase.startTimestamp === 0;
      const phraseGraphic = new PhraseGraphic(
        this,
        x,
        {
          globalScaling: 1,
          pitchLineGap: 20,
          pitchLineWidth: 1 * window.devicePixelRatio,
          measureLineWidth: 2 * window.devicePixelRatio,
          phraseWidth: 1660,
          clefGap: 100,
          doubleEndLines: false,
        },
        phrase,
        "None",
        this.measureCount,
        undefined,
        this.currentIdx % 2 === 1,
      );
      this.measureCount += phrase.measureData.length; // Hack/Jank: This is hardcoded for now, maybe look into making it dynamic
      const idx = this.currentIdx;
      this.phrases[(idx + 2) % 4]?.graphic.transitionOut(() => {
        this.phrases[(idx + 2) % 4]?.destroy();
        this.phrases[idx ?? 0]?.graphic.transition("Fade");
      }, this);
      const cameraHeight = this.cameras.main.height;
      const phraseGap = 50 * window.devicePixelRatio;
      const availVertSpace = phraseGraphic.getHeight() * 2 + phraseGap;
      const paddingVert = (cameraHeight - availVertSpace) / 2;
      const phraseY = !this.mobileView
        ? paddingVert +
          (this.currentIdx % 2 === 1
            ? phraseGraphic.getHeight() + phraseGap
            : 0)
        : this.cameras.main.height / 2 - phraseGraphic.getHeight() / 2;
      phraseGraphic.setPosition(x, phraseY);
      const cursorHeight = phraseGraphic.getHeight() * 1.35;
      const cursorWidth = cursorHeight / 10;
      console.debug("width", cursorWidth, "height", cursorHeight);
      const cursorY =
        phraseGraphic.getHeight() / 2 - cursorHeight / 2 + phraseGraphic.y;
      const firstNotePosition = phraseGraphic.getFirstNotePosition(0);
      const phraseCursor = new CursorMobile(
        this,
        firstNotePosition,
        cursorY,
        cursorWidth,
        cursorHeight,
        this.eventStream!,
        new IteratorExtensible(this, [], phraseRaw, phrase.timeSignature),
        phraseData.cursorData,
        this.mobileView,
        this.onPlayingLastSlice,
        this.onFinishPlayingPhrase,
        this,
        phrase.startTimestamp > 0,
        phrase.startTimestamp,
        this.currentIdx,
        this.phrases.length === 0,
      );
      phraseCursor.reset(
        new IteratorExtensible(this, [], phraseRaw, phrase.timeSignature),
        phrase.startTimestamp,
        phraseGraphic.phraseStartingMeasure,
      );
      console.debug("phrase idx", this.currentIdx);
      if (this.phrases.length === 0) {
        this.phrases[0] = new PlayablePhrase(phraseGraphic, phraseCursor);
      } else {
        const idx = this.currentIdx;
        if (this.phrases.length >= 4 && this.currentIdx !== undefined) {
          console.log("destroyed", this.currentIdx);
          // Clean up error feedback for destroyed phrase
          const destroyedPhrase = this.phrases[idx];
          if (destroyedPhrase) {
            this.errorFeedbackManager?.cleanupPhrase(
              destroyedPhrase.graphic._data.uuid,
            );
          }
          this.phrases[idx]?.destroy();
        }
        this.phrases[this.currentIdx] = new PlayablePhrase(
          phraseGraphic,
          phraseCursor,
        );
      }
      !this.mobileView &&
        this.resize({ width: this.width, height: this.height });
      phraseCursor.addPhrase(cursorData, phraseRaw.uuid, phrase.timeSignature);
      this.eventStream?.addEvents2(
        new Iterator(cursorData, phraseRaw),
        phraseRaw.getStartTimestamp(),
      );
      this.phrasesSeen.set(phrase.uuid, phrase.startTimestamp);
      this.firstPhrase = false;
    } else {
    }
  }

  getNoteById(id: string, phraseStart?: number): IPlayable | null {
    const note = this.phrases.map((phrase) => ({
      note: phrase?.graphic.getNoteById(id),
      phraseStart,
    }));
    note.forEach((n) => {
      if (n.note !== undefined && n.phraseStart !== phraseStart) {
      }
    });
    return note.map((n) => n.note).find((n) => n !== undefined) ?? null;
  }

  // The update function might still be running when the Puase callback is triggered by
  // the registered event which will mess up the validity of the state.
  // Therefore, we queue a pause request which then immediately gets processed
  // in the next update() tick.
  pauseExercise() {
    this.pauseQueued = true;
  }

  _pauseExercise() {
    this.shouldUpdate = false;
    //this.cursors[this.currentCursor].clearBlink();
    this.lastTickTime = undefined;
    //this.cursors[this.currentCursor].pause();
    this.phrases[this.activePhrase]?.pause();
    const lastPlayedMeasure = Math.max(
      this.phrases[this.activePhrase]!.cursor!.getLastPlayedMeasure() - 1,
      0,
    );
    NotifyReact(ExercisePaused(lastPlayedMeasure));
    this.pauseQueued = false;
    this.setCursor({});
  }

  getCurrentPhrase() {
    let currentPhrase: PhraseGraphic = this.phrases[0]!.graphic;
    const currentTimestamp =
      this.eventStream!.timeKeeper.audioTimeToMeasureTimestamp();
    for (const phrase of this.phrases) {
      if (phrase && phrase.graphic._data.startTimestamp > currentTimestamp)
        continue;
      else if (
        phrase &&
        phrase.graphic._data.startTimestamp > currentPhrase._data.startTimestamp
      ) {
        currentPhrase = phrase.graphic;
      }
    }
    return currentPhrase;
  }

  setNoteState({
    id,
    state,
    colorblindType,
  }: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
  }) {
    this.getNoteById(
      id,
      this.getCurrentPhrase()._data.startTimestamp,
    )?.setTimingState(state, colorblindType);
  }

  pulseNote({
    id,
    colorblindType,
  }: {
    id: string;
    colorblindType?: COLORBLIND_TYPE | null;
  }) {
    this.getNoteById(id, this.getCurrentPhrase()._data.startTimestamp)?.pulse(
      colorblindType,
    );
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean) {
    //this.phrases.map
    //return this.getNoteById(id)?.getPosition().x ?? null;
    const x =
      this.phrases
        .map((p, idx) => {
          return p?.graphic.getNotePosition(id, timestamp, rest);
        })
        .find((x) => x !== undefined) ?? null;
    return x;
  }

  getPhraseEndPosition() {
    return Math.max(
      ...this.phrases
        .filter((p) => p !== undefined)
        .map((phrase) => phrase.graphic.getNextPosition()),
    );
  }

  setNoteColor(
    color: string | number,
    id?: string,
    colorblindType?: COLORBLIND_TYPE | null,
  ) {
    if (id === undefined)
      // target first note in current phrase
      id = this.phrases[this.activePhrase]?.graphic.getFirstNoteId();
    if (id === undefined) return;
    this.getNoteById(
      id,
      this.getCurrentPhrase()._data.startTimestamp,
    )?.setColor(color, colorblindType);
  }

  /**
   * Get the current cursor position and active phrase.
   * Used by error feedback system to spawn visuals at cursor location.
   */
  getCursorPosition(): { x: number; phrase: PlayablePhrase } | null {
    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase || !activePhrase.cursor) {
      return null;
    }
    return {
      x: activePhrase.cursor.x,
      phrase: activePhrase,
    };
  }

  startPlaying() {
    this.timeKeeperAuxiliary?.pause();
    this.timeKeeperAuxiliary?.resetTime();
    //const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
    this.phrases[0]?.activate();
    this.phrases[0]?.cursor?.unpause(0);
    //this.cursors[0].unpause(measureOffset);
    //this.eventStream!.timeKeeper.resetTime();
    NotifyReact(StartedPlaying());
    this.playbackStarted = true;
    //console.debug('current cursor', this.currentCursor);
    //this.cursors[this.currentCursor].clearBlink();
    //this.cursors[this.currentCursor].enabled = true;
  }

  noteOn({ note, velocity }: { note: number; velocity: number }) {
    console.debug("note down", note, velocity);
    if (this.playbackStarted && !this.gameplayStarted) {
      this.eventsQueue.push([note, velocity]);
    }
    if (!this.playbackStarted && this.eventStream !== undefined) {
      const timestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      if (!this.lastTickTime) return;
      const timeDiff = timestamp - this.lastTickTime;
      console.log("timeoffset timediff: ", timestamp, timeDiff);
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        //this.showToast(`Too Late!`);
        NotifyReact(ShowToast("Too late!"));
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        //this.showToast(`Too early!`);
        NotifyReact(ShowToast("Too early!"));
      } else {
        this.timeKeeperAuxiliary?.pause();
        this.timeKeeperAuxiliary?.resetTime();
        const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.phrases[0]?.activate();
        this.phrases[0]?.cursor?.unpause(measureOffset);
        this.eventsQueue.push([note, velocity]);
        NotifyReact(
          StartedPlaying({
            measureOffset,
            processEventsQueue: [this.processEventsQueue, this],
          }),
        );
        this.playbackStarted = true;
      }
    }
  }

  processEventsQueue() {
    console.debug("processing events queue", this.eventsQueue);
    this.eventsQueue.forEach(([note, velocity]) => {
      if (velocity === 0) this.eventStream?.handleNoteOffEvent(note);
      else this.eventStream?.handleNoteOnEvent(note, velocity);
    });
    this.eventsQueue = [];
  }

  noteOff({ note }: { note: number }) {}

  destroy() {
    //this.cursors.forEach((c) => c.destroy(true));
    //this.cursors.clear();
    // Clean up error feedback for all phrases
    this.errorFeedbackManager?.cleanupAll();
    this.phrases.forEach((p) => p?.destroy());
    this.phrases.clear();
    RemoveListener(ReactToExerciseEventType.SetCursor);
    RemoveListener(ReactToExerciseEventType.AddPhrase);
    RemoveListener(ReactToExerciseEventType.ResizeWindow);
    RemoveListener(ReactToExerciseEventType.SetFreePlayMode);
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
  }

  newResize({ width, height }: { width: number; height: number }) {
    if (this.phrases[0] === undefined) return;
    const {
      paddingX,
      paddingY,
      phraseRenderingConfig,
      staffSpacingVertical,
      topPhraseDimens,
      bottomPhraseDimens,
    } = optimizeStaffLineHeight(
      this.phrases[0]!.cursor!.cursorData,
      this.phrases[0]!.cursor!.cursorData,
      [width, height],
      [150, 10],
    );
    const { topClefNumExtraLinesTop, topClefNumExtraLinesBottom, bottomClefNumExtraLinesTop, bottomClefNumExtraLinesBottom } = topPhraseDimens;
    console.debug('[SightReadingTrainer] Optimized values: ', phraseRenderingConfig, topPhraseDimens, bottomPhraseDimens);
    //const paddingYScaled = paddingY * window.devicePixelRatio;
    //const paddingXScaled = paddingX * window.devicePixelRatio;
    const availableWidth = width - paddingX * 2;
    console.debug('[SightReadingPhaser] availableWidth', availableWidth, 'originalWidth', width);
    this.phrases.forEach((p) => {
      //p?.graphic.bestFit(availableWidth, availableHeight, paddingXScaled, paddingYScaled)
      if (p === undefined) return;
      let targetPhraseDimens = p.graphic.bottom
        ? bottomPhraseDimens
        : topPhraseDimens;
      const phraseHeightTotal = topPhraseDimens.topClefHeight.outerHeight + topPhraseDimens.bottomClefHeight.outerHeight + phraseRenderingConfig.clefGap + 1 * phraseRenderingConfig.pitchLineGap;
      const { topClefHeight, bottomClefHeight } = targetPhraseDimens;
      const phraseHeightInner =
        topClefHeight.innerHeight +
        bottomClefHeight.innerHeight +
        1 * phraseRenderingConfig.pitchLineGap +
        phraseRenderingConfig.clefGap;
      const topPhraseHeightInnerWithoutExtraLinesAtTop = topClefHeight.innerHeight 
        + bottomClefHeight.outerHeight 
        + phraseRenderingConfig.clefGap 
        + 0.5 * phraseRenderingConfig.pitchLineGap * window.devicePixelRatio;
      console.debug('[SightReadingPhaser] staffSpacingVertical', staffSpacingVertical, topClefNumExtraLinesTop);
      const yOffset = topClefNumExtraLinesTop * .5 * phraseRenderingConfig.pitchLineGap;
      const phraseY = ((paddingY + yOffset) +
        (p.graphic.bottom ? (phraseHeightTotal + staffSpacingVertical) : 0));
      p.graphic.resize(
        phraseRenderingConfig,
        availableWidth,
        paddingX,
        /*(paddingY + topClefNumExtraLinesTop * .5 * phraseRenderingConfig.pitchLineGap +
          (p.graphic.bottom ? topPhraseHeightInnerWithoutExtraLinesAtTop + staffSpacingVertical + topClefNumExtraLinesTop * .5 * phraseRenderingConfig.pitchLineGap : 0)),*/
        phraseY,
        topPhraseDimens,
        phraseHeightInner,
      );
      const c = p?.cursor;
      if (c === undefined) return;
      c.height = phraseHeightInner * 1.25 * window.devicePixelRatio;
      c.width = c.height / 10;
      c.radius = c.width / 2;
      //const innerHeight = topPhraseDimens.topClefHeight.innerHeight + topPhraseDimens.bottomClefHeight.innerHeight + 1 * phraseRenderingConfig.pitchLineGap + phraseRenderingConfig.clefGap;
      const y = (phraseY + phraseHeightInner / 2) * window.devicePixelRatio - c.height / 2;
      c.setY(y);
      if (!this.playbackStarted && c.idx === 0) {
        const firstNote = this.phrases[0]?.graphic.getFirstNotePosition()!;
        c.setLastNotePos(firstNote - c.width / 2);
        c.setX(firstNote - c.width / 2);
      }
      //this.phrase1Gizmo?.setY(paddingY * window.devicePixelRatio);
      if (p.graphic.bottom) {
        this.phrase2Gizmo?.setSize(availableWidth * window.devicePixelRatio, phraseHeightTotal * window.devicePixelRatio);
        this.phrase2Gizmo?.setX(paddingX * window.devicePixelRatio);
        //this.phrase2Gizmo?.setY(paddingY * window.devicePixelRatio + phraseHeightTotal * window.devicePixelRatio + staffSpacingVertical * window.devicePixelRatio);
        /*this.phrase2Gizmo?.setY(
          (paddingY + topClefNumExtraLinesTop * .5 * phraseRenderingConfig.pitchLineGap +
            (p.graphic.bottom ? topPhraseHeightInnerWithoutExtraLinesAtTop + staffSpacingVertical + topClefNumExtraLinesTop * .5 * phraseRenderingConfig.pitchLineGap : 0) - topClefNumExtraLinesTop / 2 * phraseRenderingConfig.pitchLineGap) * window.devicePixelRatio
        );*/
        this.phrase2Gizmo?.setY((phraseY - yOffset) * window.devicePixelRatio);
      } else {
        this.phrase1Gizmo?.setY((phraseY - yOffset) * window.devicePixelRatio);
        this.phrase1Gizmo?.setX(paddingX * window.devicePixelRatio);
        this.phrase1Gizmo?.setSize(availableWidth * window.devicePixelRatio, phraseHeightTotal * window.devicePixelRatio);
      }
      //this.phrase2Gizmo?.setAlpha(0);
    });
  }

  resize(props: { width: number; height: number }) {
    let width = props.width;
    let height = props.height;
    this.width = width;
    this.height = height;
    //width *= window.devicePixelRatio;
    //height *= window.devicePixelRatio;
    this.scale.resize(
      width * window.devicePixelRatio,
      height * window.devicePixelRatio,
    );
    //this.pauseExercise();
    //NotifyReact(ExercisePaused());
    if (this.phrases[0] === undefined) return;
    return this.newResize(props);
  }
}
